/**
 * Team budgets (#1226): pure functions over the API's rows. The server owns
 * the money: the ledger is the customer's price, each usage row was charged
 * when it was written (spend control PR 6a), and addresses a Realm admin may
 * not see are folded into one line. Nothing here prices anything.
 *
 * Every figure a reader sees by default is the customer's PRICE. Our cost,
 * the markup and the margin reach a Manager only; a Realm admin's rows carry
 * none of them, and nothing here may name them on a Realm admin's page.
 */
import type { BillingDisplayUnit, BudgetState, TeamBudgetView, TeamChargeView, TeamUsageRow } from '../types/gotypes'
import { DATA_SOURCE_FEATURE_KEYS, FEATURE_LABELS } from '../types/tokenUsage'
import type { TokenUsageFeatureKey } from '../types/tokenUsage'

/**
 * One row of a team's month: one day × person × feature × model × job, as
 * GET /budgets/usage?group_by=detail returns it. user_id is the address, or
 * the folded line's label when the caller may not see the address.
 *
 * amount_gbp is the figure the page adds up: what the rows charged, unless
 * the page asked for our cost (the Manager-only AI usage page). charged_gbp
 * is always the charge; our_cost_gbp is present on a Manager's rows only.
 */
export interface BudgetRow {
  id: string; created_at: number; date: string
  team_id: string; team_name: string
  user_id: string; user_label: string
  feature_key: string; feature_label: string; model: string; job: string
  runs: number; total_tokens: number
  amount_gbp: number
  charged_gbp: number
  our_cost_gbp?: number
  /** Priced at the card's dearest rate: the model is not on the rate card. */
  unpriced: boolean
  /** Calls the provider's content filter refused (migration 000167): their own detail row, tokens estimated. */
  content_filtered?: boolean
}

/** Which figure a page adds up: what was charged (every budget page), or our cost (AI usage, Manager only). */
export type AmountBasis = 'price' | 'cost'

/** A usage row's charge and, on a Manager's row only, our cost. */
export function usageMoney(r: TeamUsageRow): { charged: number; ourCost?: number } {
  return r.cost_gbp !== undefined ? { charged: r.charged_gbp, ourCost: r.cost_gbp } : { charged: r.charged_gbp }
}

/**
 * Agent work (#1886): the server serves each agent call under what its chat
 * made, as `agent:<category>`. A chat counts once, under its main output
 * (web app first, then dashboard, alert, video, PowerPoint, document, Excel,
 * skill, table, image); a chat that made none of them is `agent:chat`. A
 * plain `agent` row is a call whose chat is not known (logged before chats
 * were recorded, or the output log could not be read).
 */
export const AGENT_OUTPUT_LABELS: Record<string, string> = {
  'agent:webapp': 'Agent: web app',
  'agent:dashboard': 'Agent: dashboard',
  'agent:alert': 'Agent: alert',
  'agent:video': 'Agent: video',
  'agent:deck': 'Agent: PowerPoint',
  'agent:document': 'Agent: document',
  'agent:workbook': 'Agent: Excel',
  'agent:skill': 'Agent: skill',
  'agent:table': 'Agent: table',
  'agent:image': 'Agent: image',
  'agent:chat': 'Agent: analysis or chat',
}

/** The "Agent work" label of a plain `agent` row: its chat is not known. */
export const AGENT_OUTPUT_UNKNOWN_LABEL = 'Agent: output not known'

/** A feature key without its agent category: `agent:webapp` is `agent`; any other key is itself. */
export function baseFeatureKey(key: string): string {
  return key.startsWith('agent:') ? 'agent' : key
}

export function featureLabel(key: string): string {
  return AGENT_OUTPUT_LABELS[key] ?? FEATURE_LABELS[key as TokenUsageFeatureKey] ?? key
}

/**
 * What a usage row is billed as (Kai, 3 Nov 2026): a data source (X, Apify,
 * YouTube; DATA_SOURCE_FEATURE_KEYS) or AI (every other feature).
 */
export type UsageCategory = 'ai' | 'data_source'
export function usageCategory(featureKey: string): UsageCategory {
  return (DATA_SOURCE_FEATURE_KEYS as readonly string[]).includes(baseFeatureKey(featureKey)) ? 'data_source' : 'ai'
}

/** One line of the split: the page's figure (amount_gbp), the charge, and the calls. */
export interface CategoryLine { gbp: number; charged: number; runs: number }
export interface DataSourceLine extends CategoryLine { key: string; label: string }
export interface CategorySplit { ai: CategoryLine; dataSources: CategoryLine & { sources: DataSourceLine[] } }

/** The rows' totals as AI and data sources, with every data source on its own line (one at £0 included), in the list's order. */
export function categorySplit(rows: BudgetRow[]): CategorySplit {
  const ai: CategoryLine = { gbp: 0, charged: 0, runs: 0 }
  const sources: DataSourceLine[] = DATA_SOURCE_FEATURE_KEYS.map((key) => ({ key, label: featureLabel(key), gbp: 0, charged: 0, runs: 0 }))
  for (const r of rows) {
    const line = usageCategory(r.feature_key) === 'ai' ? ai : sources.find((s) => s.key === baseFeatureKey(r.feature_key))!
    line.gbp += r.amount_gbp; line.charged += r.charged_gbp; line.runs += r.runs
  }
  const tidy = <T extends CategoryLine>(l: T): T => ({ ...l, gbp: round2(l.gbp), charged: round2(l.charged) })
  const all = sources.map(tidy)
  return {
    ai: tidy(ai),
    dataSources: {
      gbp: round2(all.reduce((s, l) => s + l.gbp, 0)), charged: round2(all.reduce((s, l) => s + l.charged, 0)),
      runs: all.reduce((s, l) => s + l.runs, 0), sources: all,
    },
  }
}

export function rowsFromUsage(rows: TeamUsageRow[], basis: AmountBasis = 'price'): BudgetRow[] {
  return rows.map((r) => {
    const date = r.date ?? ''
    const user = r.user_id || r.label
    const { charged, ourCost } = usageMoney(r)
    return {
      id: [date, r.team_id, user, r.feature_key ?? '', r.model ?? '', r.job ?? '', r.outcome ?? ''].join('|'),
      created_at: date ? Date.parse(date + 'T00:00:00Z') : 0,
      date, team_id: r.team_id, team_name: r.team_name,
      user_id: user, user_label: r.label,
      feature_key: r.feature_key ?? '', feature_label: featureLabel(r.feature_key ?? ''),
      model: r.model ?? '', job: r.job ?? '',
      runs: r.request_count, total_tokens: r.total_tokens,
      amount_gbp: basis === 'cost' && ourCost !== undefined ? ourCost : charged,
      charged_gbp: charged,
      ...(ourCost !== undefined ? { our_cost_gbp: ourCost } : {}),
      unpriced: !!r.unpriced,
      ...(r.outcome === 'content_filtered' ? { content_filtered: true } : {}),
    }
  })
}

/**
 * Fixed-mode charges (spend control PR 6c) as month-table rows: "What" is
 * the action (Agent turn, ThemeAI run, …), "Calls" its quantity (turns, units
 * of 100 responses, records), and the amount the list price it was booked
 * at. A charge has no model or tokens; the calls inside it are usage rows
 * charged £0. A charge carries no cost, so our_cost_gbp is never set.
 */
export function rowsFromCharges(charges: TeamChargeView[]): BudgetRow[] {
  return charges.map((c) => {
    const user = c.user_id || c.label
    return {
      id: `charge|${c.id}`,
      created_at: c.date ? Date.parse(c.date + 'T00:00:00Z') : c.created_at,
      date: c.date, team_id: c.team_id, team_name: c.team_name,
      user_id: user, user_label: c.label,
      feature_key: `charge:${c.kind}`, feature_label: c.what,
      model: '', job: c.job ?? '',
      runs: c.quantity, total_tokens: 0,
      amount_gbp: c.charged_gbp,
      charged_gbp: c.charged_gbp,
      unpriced: false,
    }
  })
}

/** The band colours: Matt's 50 / 75 / 90 per cent of the allocation (#1226). */
export type Threshold = 'ok' | '50' | '75' | '90' | 'used'

/** The alert thresholds every customer had before they became settable. */
export const DEFAULT_ALERT_THRESHOLDS = [50, 75, 90, 100]

const bandOf = (t: number): Threshold => (t >= 90 ? '90' : t >= 75 ? '75' : '50')

/**
 * The band colour for a team that has used `pct` per cent of its budget.
 * With the team's own alert thresholds (the Manager's list, spend control
 * 6b), the band is the one the highest threshold already crossed falls in, so
 * a team on {80} turns amber at 80%; no thresholds crossed, or an empty
 * list, is 'ok'. Without a list it reads the default 50 / 75 / 90 / 100.
 * At 100% or more the budget is 'used', whatever the list.
 */
export function thresholdOf(pct: number, thresholds: number[] = DEFAULT_ALERT_THRESHOLDS): Threshold {
  // A used budget is a fact, not an alert setting: 'used' whatever the list.
  if (pct >= 100) return 'used'
  const crossed = thresholds.filter((t) => t >= 1 && t <= 100 && pct >= t)
  return crossed.length === 0 ? 'ok' : bandOf(Math.max(...crossed))
}

/**
 * A team has a budget once it has a budget year (annual budgets, migration
 * 000145), whatever its state: an ended or scheduled year keeps its figures on
 * screen. budgetState says whether the year is live.
 */
export function hasBudget(b: TeamBudgetView): boolean {
  return b.period_id != null
}

/** The team's budget state now; "none" when the server sent none. */
export function budgetState(b: Pick<TeamBudgetView, 'budget_state'>): BudgetState {
  return b.budget_state ?? 'none'
}

/**
 * What a budget card says about a year that is not live: "Starts 1 Jan 2027"
 * for a scheduled year, "Year ended 31 Dec 2026" for one that ended (the end
 * is exclusive, so the last day is a millisecond before it). Empty for an
 * active year, or when the window is unknown.
 */
export function yearStateLabel(b: Pick<TeamBudgetView, 'budget_state' | 'budget_start_at' | 'budget_end_at'>): string {
  const state = budgetState(b)
  if (state === 'scheduled' && b.budget_start_at != null) return `Starts ${shortDay(b.budget_start_at)}`
  if (state === 'ended' && b.budget_end_at != null) return `Year ended ${shortDay(b.budget_end_at - 1)}`
  return ''
}

/** One day in milliseconds. */
export const DAY_MS = 86_400_000

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** A UTC day as a card shows it: "30 Sep 2027" (spelled out, not ICU's, which says "Sept"). */
export const shortDay = (ms: number): string => {
  const d = new Date(ms)
  return `${d.getUTCDate()} ${SHORT_MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`
}

/**
 * The exclusive end of a budget year starting at startMs: the same date and
 * time a year on, in UTC (types.BudgetYearEnd). Date.UTC rolls 29 February
 * to 1 March, as Go's AddDate does.
 */
export function budgetYearEnd(startMs: number): number {
  const d = new Date(startMs)
  return Date.UTC(d.getUTCFullYear() + 1, d.getUTCMonth(), d.getUTCDate(), d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds(), d.getUTCMilliseconds())
}

/** The UTC date of an instant, as a date box holds it: "2026-09-30". */
export const dateInputOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10)

/** A date box's YYYY-MM-DD as 00:00 UTC that day; null when it is not a date. */
export function parseDateInput(v: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return null
  const ms = Date.parse(v + 'T00:00:00Z')
  return Number.isNaN(ms) || dateInputOf(ms) !== v ? null : ms
}

/**
 * The period a usage page reads (Kai, 3 Nov 2026): one calendar month (the
 * default), or a from–to range of UTC dates, both days included, of at most
 * MAX_RANGE_DAYS (the server's cap, maxUsageRangeDays in server/budgets.go).
 */
export type UsagePeriod = { kind: 'month'; month: string } | { kind: 'range'; from: string; to: string }
export const MAX_RANGE_DAYS = 366

/** The usage route's query for a period: ?month=, or the half-open ?from=&to= (the To day included, so `to` is the day after). */
export function periodParams(p: UsagePeriod): Record<string, string> {
  if (p.kind === 'month') return { month: p.month }
  const to = parseDateInput(p.to)
  return { from: p.from, to: to === null ? p.to : dateInputOf(to + DAY_MS) }
}

/** "September 2026", "1 Sep 2026 – 14 Oct 2026", or "3 Sep 2026" for a one-day range. */
export function periodLabel(p: UsagePeriod): string {
  if (p.kind === 'month') return monthLabel(p.month)
  const from = parseDateInput(p.from); const to = parseDateInput(p.to)
  if (from === null || to === null) return `${p.from} – ${p.to}`
  return from === to ? shortDay(from) : `${shortDay(from)} – ${shortDay(to)}`
}

/** Why a from–to range (both days included) cannot be read, or null when it can. */
export function rangeError(from: string, to: string): string | null {
  if (!from || !to) return 'Pick both a start and an end date.'
  const f = parseDateInput(from); const t = parseDateInput(to)
  if (f === null || t === null) return 'Pick a valid date.'
  if (t < f) return 'The end date must be on or after the start date.'
  if ((t - f) / DAY_MS + 1 > MAX_RANGE_DAYS) return `A range is at most ${MAX_RANGE_DAYS} days.`
  return null
}

/** A year by its first and last day (end exclusive): "1 Oct 2026 – 30 Sep 2027". */
export const yearRangeLabel = (startMs: number, endMs: number): string => `${shortDay(startMs)} – ${shortDay(endMs - 1)}`

/** Whole days from nowMs to a year's exclusive end, rounded up; 0 once it has ended. */
export const daysLeftIn = (endMs: number, nowMs: number): number => Math.max(0, Math.ceil((endMs - nowMs) / DAY_MS))

/**
 * What a budget card says about a team's year: "Year to 30 Sep 2027" while
 * it runs, "Year ended 30 Sep 2027" once over, "Starts 1 Oct 2027" before it
 * begins; empty for a team with no year.
 */
export function yearLabel(b: Pick<TeamBudgetView, 'budget_state' | 'budget_start_at' | 'budget_end_at'>): string {
  if (budgetState(b) === 'active' && b.budget_end_at != null) return `Year to ${shortDay(b.budget_end_at - 1)}`
  return yearStateLabel(b)
}

/**
 * What a team used per day in the year it is shown: spent ÷ days since the
 * year started (fractional, never under one day; up to the year's end once it
 * ended). Null for a team with no year, or one whose year has not started.
 */
export function averageDailySpend(
  b: Pick<TeamBudgetView, 'spent_gbp' | 'budget_start_at' | 'budget_end_at' | 'budget_state' | 'period_id'>,
  nowMs = Date.now(),
): { perDay: number; days: number } | null {
  const state = budgetState(b)
  if (b.period_id == null || b.budget_start_at == null || (state !== 'active' && state !== 'ended')) return null
  const days = Math.max(1, (Math.min(nowMs, b.budget_end_at ?? nowMs) - b.budget_start_at) / DAY_MS)
  // Not rounded here: the formatter rounds once, after the credit conversion.
  return { perDay: b.spent_gbp / days, days }
}

/** A team the User management switch would leave without AI, and why. */
export interface UnbudgetedTeam { name: string; state: BudgetState }

/**
 * The User management switch's warning (Adam, 6 Oct): which teams with people
 * in them have no AI with the switch on, and the real reason for each: no
 * budget, a year that ended, or one that has not started. Empty for none.
 */
export function unbudgetedWarning(teams: UnbudgetedTeam[], enforced: boolean): string {
  if (teams.length === 0) return ''
  const named = (state: BudgetState) => teams.filter((t) => t.state === state).map((t) => t.name)
  const none = named('none'), ended = named('ended'), scheduled = named('scheduled')
  const parts: string[] = []
  if (none.length) parts.push(`${none.join(', ')}: no budget is set`)
  if (ended.length) parts.push(`${ended.join(', ')}: ${ended.length === 1 ? 'its budget year' : 'their budget years'} ended`)
  if (scheduled.length) parts.push(`${scheduled.join(', ')}: ${scheduled.length === 1 ? 'its budget year has' : 'their budget years have'} not started yet`)
  const action = parts.length > 1 ? 'See AI budgets'
    : none.length ? 'Set one on AI budgets'
    : ended.length ? 'Start the next year on AI budgets'
    : `Change ${scheduled.length === 1 ? 'its' : 'their'} start date on AI budgets`
  return enforced
    ? `AI is off for ${parts.join('; ')}. ${action}.`
    : `Turning this on stops all AI for ${parts.join('; ')}. ${action} first.`
}

export interface Slice { key: string; label: string; runs: number; gbp: number; tokens: number }
export interface WeekPoint { weekStart: string; gbp: number; runs: number }

const round2 = (n: number) => Math.round(n * 100) / 100

export function slices(rows: BudgetRow[], key: (r: BudgetRow) => string, label: (r: BudgetRow) => string): Slice[] {
  const m = new Map<string, Slice>()
  for (const r of rows) {
    const k = key(r)
    const s = m.get(k) ?? { key: k, label: label(r), runs: 0, gbp: 0, tokens: 0 }
    s.runs += r.runs; s.gbp += r.amount_gbp; s.tokens += r.total_tokens
    m.set(k, s)
  }
  return [...m.values()].map((s) => ({ ...s, gbp: round2(s.gbp) })).sort((a, b) => b.gbp - a.gbp)
}

/** ISO date of the Monday on or before the timestamp, in UTC. */
export function weekStartOf(ms: number): string {
  const d = new Date(ms)
  const day = (d.getUTCDay() + 6) % 7
  const monday = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - day))
  return monday.toISOString().slice(0, 10)
}

export function weeklySeries(rows: BudgetRow[]): WeekPoint[] {
  const m = new Map<string, WeekPoint>()
  for (const r of rows) {
    const w = weekStartOf(r.created_at)
    const p = m.get(w) ?? { weekStart: w, gbp: 0, runs: 0 }
    p.gbp += r.amount_gbp; p.runs += r.runs
    m.set(w, p)
  }
  return [...m.values()].map((p) => ({ ...p, gbp: round2(p.gbp) })).sort((a, b) => a.weekStart.localeCompare(b.weekStart))
}

/**
 * What a reader can hone a team's month by (Adam, 13 Sep: "filter by every
 * factor … and make it update live"). Every field is AND-ed; an empty list or
 * string means "any". `from`/`to` are ISO dates, inclusive, in UTC.
 */
export interface RunFilter { teams: string[]; people: string[]; features: string[]; models: string[]; jobs: string[]; from: string; to: string; text: string }
export const EMPTY_FILTER: RunFilter = { teams: [], people: [], features: [], models: [], jobs: [], from: '', to: '', text: '' }
export function isFilterEmpty(f: RunFilter): boolean {
  return !f.teams.length && !f.people.length && !f.features.length && !f.models.length && !f.jobs.length && !f.from && !f.to && !f.text.trim()
}

export function filterRows(rows: BudgetRow[], f: RunFilter): BudgetRow[] {
  if (isFilterEmpty(f)) return rows
  const fromMs = f.from ? Date.parse(f.from + 'T00:00:00Z') : -Infinity
  const toMs = f.to ? Date.parse(f.to + 'T23:59:59.999Z') : Infinity
  const needle = f.text.trim().toLowerCase()
  return rows.filter((r) => {
    // Team keys on team_id ('' is "No team"), never on the name.
    if (f.teams.length && !f.teams.includes(r.team_id)) return false
    if (f.people.length && !f.people.includes(r.user_id)) return false
    // A feature matches on its full key ("agent:webapp") or its base ("agent").
    if (f.features.length && !f.features.includes(r.feature_key) && !f.features.includes(baseFeatureKey(r.feature_key))) return false
    if (f.models.length && !f.models.includes(r.model)) return false
    if (f.jobs.length && !f.jobs.includes(r.job)) return false
    if (r.created_at < fromMs || r.created_at > toMs) return false
    if (needle) {
      const hay = [r.user_label, r.user_id, r.feature_label, r.feature_key, r.model, r.job].join(' ').toLowerCase()
      if (!hay.includes(needle)) return false
    }
    return true
  })
}

/**
 * The What filter's options: each feature in the rows, by label, plus one
 * "Agent" option (every agent call, split or not) when any agent row is
 * split by what its chat made (#1886), the same key the By feature bar sets.
 */
export function featureOptions(rows: BudgetRow[]): { value: string; label: string }[] {
  const m = new Map<string, string>()
  for (const r of rows) {
    if (!m.has(r.feature_key)) m.set(r.feature_key, r.feature_label)
    if (r.feature_key.startsWith('agent:') && !m.has('agent')) m.set('agent', featureLabel('agent'))
  }
  return [...m].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
}

/** The last day of the week that starts on `weekStart` (a Monday), as an ISO date. */
export function weekEndOf(weekStart: string): string {
  return new Date(Date.parse(weekStart + 'T00:00:00Z') + 6 * 86_400_000).toISOString().slice(0, 10)
}

/**
 * A month's figures over `rows`: the whole month, or the rows a filter kept.
 * byFeature puts every agent row under one "Agent" slice; byAgentOutput
 * splits the agent rows by what their chat made (#1886).
 */
export interface MonthTotals {
  runs: number; spent: number; tokens: number
  byFeature: Slice[]; byAgentOutput: Slice[]; byModel: Slice[]; byUser: Slice[]; weekly: WeekPoint[]
}
export function monthTotals(rows: BudgetRow[]): MonthTotals {
  const agentRows = rows.filter((r) => baseFeatureKey(r.feature_key) === 'agent')
  return {
    runs: rows.reduce((s, r) => s + r.runs, 0),
    spent: round2(rows.reduce((s, r) => s + r.amount_gbp, 0)),
    tokens: rows.reduce((s, r) => s + r.total_tokens, 0),
    byFeature: slices(rows, (r) => baseFeatureKey(r.feature_key), (r) => featureLabel(baseFeatureKey(r.feature_key))),
    byAgentOutput: slices(agentRows, (r) => r.feature_key, (r) => (r.feature_key === 'agent' ? AGENT_OUTPUT_UNKNOWN_LABEL : r.feature_label)),
    byModel: slices(rows, (r) => r.model, (r) => r.model),
    byUser: slices(rows, (r) => r.user_id, (r) => r.user_label),
    weekly: weeklySeries(rows),
  }
}

export interface CustomerTotals {
  allocated: number; spent: number; remaining: number; pct: number; teams: number
  /** Teams with a budget year that are NOT counted: their year ended, or has not started. */
  ended: number; scheduled: number
  /** What the spend cost us and the margin on it, when the server sent them (a Manager). */
  ourCost?: { spent: number; marginPct: number }
}
/**
 * The customer's teams added up, in price. Only teams in a LIVE budget year
 * count: an ended year's allocation can no longer be spent and a scheduled
 * one's cannot be yet, so adding them would show money nobody can use. The
 * teams left out are counted, so the page can say so.
 */
export function customerTotals(budgets: TeamBudgetView[]): CustomerTotals {
  const withBudget = budgets.filter((b) => hasBudget(b) && budgetState(b) === 'active')
  const allocated = round2(withBudget.reduce((s, b) => s + b.allocated_gbp, 0))
  const spent = round2(withBudget.reduce((s, b) => s + b.spent_gbp, 0))
  const totals: CustomerTotals = {
    allocated, spent, remaining: round2(allocated - spent), pct: allocated > 0 ? (spent / allocated) * 100 : spent > 0 ? 100 : 0, teams: withBudget.length,
    // An archived team holds nobody and spends nothing: it is not named.
    ended: budgets.filter((b) => !b.archived && hasBudget(b) && budgetState(b) === 'ended').length,
    scheduled: budgets.filter((b) => !b.archived && hasBudget(b) && budgetState(b) === 'scheduled').length,
  }
  if (withBudget.length > 0 && withBudget.every((b) => b.cost_spent_gbp !== undefined)) {
    const costSpent = round2(withBudget.reduce((s, b) => s + (b.cost_spent_gbp ?? 0), 0))
    totals.ourCost = { spent: costSpent, marginPct: spent > 0 ? round2(((spent - costSpent) / spent) * 100) : 0 }
  }
  return totals
}

/** The price a cost figure reads at, at the customer's current markup: what the server writes for allocation_cost_gbp. */
export function priceForCost(cost: number, markupPct: number): number {
  return round2(cost * (1 + markupPct / 100))
}

/** The cost a price stands for at the customer's current markup. */
export function costForPrice(price: number, markupPct: number): number {
  return round2(price / (1 + markupPct / 100))
}

/** A price in credits: price ÷ the credit value (£1 when unset). */
export function toCredits(gbp: number, creditValueGBP: number): number {
  return gbp / (creditValueGBP > 0 ? creditValueGBP : 1)
}

/** 15000 → "15,000 credits", 1.17 → "1.17 credits". */
export function formatCredits(credits: number, dp?: number): string {
  const sign = credits < 0 ? '−' : ''
  const abs = Math.abs(credits)
  const places = dp ?? (Number.isInteger(Math.round(abs * 100) / 100) ? 0 : 2)
  return `${sign}${abs.toLocaleString('en-GB', { minimumFractionDigits: places, maximumFractionDigits: places })} credit${abs === 1 ? '' : 's'}`
}

/** A price in the unit the customer reads money in. */
export function formatAmount(gbp: number, unit: BillingDisplayUnit, creditValueGBP: number, dp = 2): string {
  return unit === 'credits' ? formatCredits(toCredits(gbp, creditValueGBP)) : formatGBP(gbp, dp)
}

/** How a customer reads money: the unit and what one credit is worth in pounds (BudgetBillingView fits). */
export interface MoneyUnit { display_unit: BillingDisplayUnit; credit_value_gbp: number }

/** Formats a price in pounds as the reader sees it. */
export type MoneyFormat = (gbp: number, dp?: number) => string

/**
 * The one formatter for money a page shows (spend control PR 6d). In pounds
 * it is formatGBP. In credits it is price ÷ credit value as "N credits"; a
 * Manager (showBoth) reads both, "N credits (£N)", since a Manager works in
 * pounds and credits side by side. dp is the pounds precision; credits take
 * it only past the penny (the month table's four places), otherwise they
 * show whole credits when they are whole.
 */
export function moneyFormatter(unit: MoneyUnit | undefined, showBoth = false): MoneyFormat {
  return (gbp, dp = 2) => {
    if (!unit || unit.display_unit !== 'credits') return formatGBP(gbp, dp)
    const credits = formatCredits(toCredits(gbp, unit.credit_value_gbp), dp > 2 ? dp : undefined)
    return showBoth ? `${credits} (${formatGBP(gbp, dp)})` : credits
  }
}

/** 50 → "50%", 37.5 → "37.5%". */
export function formatPct(n: number): string {
  return `${Number.isInteger(n) ? n : Math.round(n * 100) / 100}%`
}

export function formatGBP(n: number, dp = 2): string {
  const sign = n < 0 ? '−' : ''
  return sign + '£' + Math.abs(n).toLocaleString('en-GB', { minimumFractionDigits: dp, maximumFractionDigits: dp })
}

/** "2026-08" → "August 2026". */
export function monthLabel(month: string): string {
  const [y, m] = month.split('-').map(Number)
  if (!y || !m) return month
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

/** The current month as YYYY-MM, in UTC like the server. */
export function currentMonth(now = new Date()): string {
  return now.toISOString().slice(0, 7)
}

/**
 * The download: every row of the month, to four decimal places. amount_gbp
 * is what the row charged; a Manager's download (rows that carry our cost)
 * adds our_cost_gbp. A Realm admin's names neither cost nor markup.
 * `category` is ai or data_source (usageCategory), so a reader can total each.
 */
export function rowsToCSV(rows: BudgetRow[], unit?: MoneyUnit, manager = false): string {
  const withCost = rows.length > 0 && rows.every((r) => r.our_cost_gbp !== undefined)
  // A credits customer's Realm admin reads credits and no pounds; a Manager
  // gets both columns.
  const credits = unit?.display_unit === 'credits'
  const pounds = !credits || manager
  const head = ['date', 'person', 'team', 'category', 'feature', 'model', 'job', 'calls', 'total_tokens',
    ...(pounds ? ['amount_gbp'] : []), ...(credits ? ['amount_credits'] : []), ...(withCost ? ['our_cost_gbp'] : [])]
  const esc = (v: string | number) => {
    const s = String(v)
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s
  }
  const lines = rows.map((r) =>
    [r.date, r.user_label, r.team_name, usageCategory(r.feature_key), r.feature_label, r.model, r.job, r.runs, r.total_tokens,
      ...(pounds ? [r.charged_gbp.toFixed(4)] : []),
      ...(credits && unit ? [toCredits(r.charged_gbp, unit.credit_value_gbp).toFixed(4)] : []),
      ...(withCost ? [(r.our_cost_gbp ?? 0).toFixed(4)] : [])].map(esc).join(','))
  return [head.join(','), ...lines].join('\n') + '\n'
}

/**
 * The download's name. A CSV carries no stamp in its body (a footer line
 * breaks parsing), so the name carries who took it and the UTC date (#1650).
 * `month` is YYYY-MM, or a period's label (periodLabel), which is kept as it is.
 */
export function csvFileName(teamName: string, month: string, who = '', now = new Date()): string {
  const parts = ['Token usage', teamName, monthLabel(month)]
  if (who.trim()) parts.push(who.trim())
  parts.push(`${now.toISOString().slice(0, 10)} UTC`)
  return `${parts.join(' - ')}.csv`
}

/**
 * What the Insights panel is told about a team (#1333): the budget, the
 * month's breakdowns and weeks, and the largest rows, as markdown a model
 * reads well. The full month is the download; this is what fits a prompt.
 * Every figure is the customer's price (what was charged), whoever reads the
 * page: the model is never told our cost, the markup or the margin, so its
 * answer cannot repeat them.
 */
export function budgetContextMarkdown(budget: TeamBudgetView, month: string, t: MonthTotals, rows: BudgetRow[], topRows = 40, scope = '', unit?: MoneyUnit): string {
  // In credits the model is told credits and no pounds, whoever reads the page.
  const gbp = moneyFormatter(unit)
  const list = (title: string, s: Slice[]) =>
    `### ${title}\n` + s.map((x) => `- ${x.label}: ${gbp(x.gbp)} over ${x.runs} call${x.runs === 1 ? '' : 's'} (${(x.tokens / 1_000_000).toFixed(1)}M tokens)`).join('\n')
  const top = [...rows].sort((a, b) => b.charged_gbp - a.charged_gbp).slice(0, topRows)
  const band = thresholdOf(budget.pct, budget.alert_thresholds)
  // A Realm admin's view carries no thresholds: say nothing about alerts
  // rather than tell the model a list that may not be this team's.
  const thresholdList = budget.alert_thresholds ? (budget.alert_thresholds.length ? budget.alert_thresholds.map((n) => `${n}%`).join(', ') : 'none set') : ''
  return [
    `# ${budget.team_name}: AI spend, ${monthLabel(month)}`,
    ...(scope ? [`The month's figures below are the rows the reader has filtered to: ${scope}. The budget is the whole team's.`] : []),
    hasBudget(budget)
      ? `Budget ${gbp(budget.allocated_gbp, 0)}. Spent ${gbp(budget.spent_gbp)} in the budget year ${budget.budget_start_at != null && budget.budget_end_at != null ? yearRangeLabel(budget.budget_start_at, budget.budget_end_at) : ''} (${Math.round(budget.pct)}%), ${gbp(budget.remaining_gbp)} remaining. ${yearStateSentence(budget)}${thresholdList ? `Thresholds: ${thresholdList}; this team is ${band === 'ok' ? 'within budget' : band === 'used' ? 'out of budget' : `past ${band}%`}.` : ''}`
      : 'No budget is set for this team yet.',
    `${monthLabel(month)}: ${gbp(t.spent)} over ${t.runs} calls, ${(t.tokens / 1_000_000).toFixed(1)}M tokens.`,
    list('By feature', t.byFeature),
    // Only when some agent call is split: a month of plain agent rows has nothing to add.
    ...(t.byAgentOutput.some((x) => x.key !== 'agent') ? [list('Agent work, by what each chat made (a chat counts once, under its main output)', t.byAgentOutput)] : []),
    list('By model', t.byModel), list('By person', t.byUser),
    '### By week', t.weekly.map((w) => `- week of ${w.weekStart}: ${gbp(w.gbp)} over ${w.runs} calls`).join('\n'),
    `### Largest ${top.length} rows (one day, person, feature, model and job each)`,
    top.map((r) => `- ${r.date} · ${r.user_label} · ${r.feature_label} · ${r.model} · ${r.job} · ${r.runs} calls · ${r.total_tokens.toLocaleString('en-GB')} tokens · ${gbp(r.charged_gbp, 4)}`).join('\n'),
  ].join('\n\n')
}

/** For the Insights context: a year that is not live says so, so its figures are not read as today's. */
function yearStateSentence(b: TeamBudgetView): string {
  const state = budgetState(b)
  if (state === 'ended' && b.budget_end_at != null) return `This budget year ended on ${shortDay(b.budget_end_at - 1)}; no next year is set, so the team has no budget now. `
  if (state === 'scheduled' && b.budget_start_at != null) return `This budget year starts on ${shortDay(b.budget_start_at)}; until then the team has no budget. `
  return ''
}

export const BUDGET_INSIGHTS_SYSTEM = `You are Platinum's AI budget analyst, talking to a finance leader about one team's AI spend. Use only the figures given. Be concrete: name the features, models and people that drive spend, and quote pounds to the penny where it helps. Say where the team could be more efficient (cheaper models for routine work, batching, fewer regenerations) and what to watch as the budget runs down. Short paragraphs, no bullet lists unless asked, British English.`

/** The system prompt for a customer who reads money in credits: figures are credits, quoted as given, never with a currency. */
export const BUDGET_INSIGHTS_SYSTEM_CREDITS = BUDGET_INSIGHTS_SYSTEM.replace('quote pounds to the penny where it helps', 'quote credits exactly as they are given, and never put a currency on them')

export const budgetInsightsSystem = (unit?: MoneyUnit): string => (unit?.display_unit === 'credits' ? BUDGET_INSIGHTS_SYSTEM_CREDITS : BUDGET_INSIGHTS_SYSTEM)

export const BUDGET_INSIGHTS_PROMPTS = [
  'Where is this team spending most, and is that what we would expect?',
  'Who and what drives this month’s spend?',
  'Where could this team be more efficient?',
  'What happens if spend continues at this rate?',
]

/** A filter in words, for the reader and the Insights panel: "Sarah Jousiffe · Agent · from 2026-09-01". */
export function describeFilter(f: RunFilter, names: Map<string, string>, featureLabels: Map<string, string>, teamNames: Map<string, string> = new Map()): string {
  const parts: string[] = []
  if (f.teams.length) parts.push(f.teams.map((t) => teamNames.get(t) ?? t).join(', '))
  if (f.people.length) parts.push(f.people.map((p) => names.get(p) ?? p).join(', '))
  if (f.features.length) parts.push(f.features.map((k) => featureLabels.get(k) ?? featureLabel(k)).join(', '))
  if (f.models.length) parts.push(f.models.join(', '))
  if (f.jobs.length) parts.push(f.jobs.join(', '))
  if (f.from && f.to) parts.push(f.from === f.to ? `on ${f.from}` : `${f.from} to ${f.to}`)
  else if (f.from) parts.push(`from ${f.from}`)
  else if (f.to) parts.push(`to ${f.to}`)
  if (f.text.trim()) parts.push(`matching "${f.text.trim()}"`)
  return parts.join(' · ')
}
